import React, { useState, useRef, useEffect } from 'react';

/**
 * PhSlider - Isolated pH slider component for smooth performance
 *
 * This component is extracted to prevent re-renders of the parent
 * PlantCatalogSidebar when the slider value changes.
 *
 * @param {Object} props
 * @param {number|null} props.value - Current pH filter value (from parent)
 * @param {Function} props.onChange - Callback when filter should be updated (debounced)
 */
const PhSlider = ({ value, onChange }) => {
  // Local state for smooth slider movement
  const [tempPhValue, setTempPhValue] = useState(value ?? 7.0);
  const [prevValue, setPrevValue] = useState(value);
  const phTimeoutRef = useRef(null);

  // Sync with parent value when it changes externally (e.g., reset) — adjusted
  // during render rather than in an effect to avoid a setState-in-effect cascade.
  if (value !== prevValue) {
    setPrevValue(value);
    setTempPhValue(value ?? 7.0);
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (phTimeoutRef.current) {
        clearTimeout(phTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Get pH color and label based on value
   */
  const getpHColor = (ph) => {
    const pHValue = ph || 7.0;
    if (pHValue < 5.5) return { color: '#EF4444', label: 'Sauer' };
    if (pHValue < 6.5) return { color: '#F59E0B', label: 'Leicht sauer' };
    if (pHValue < 7.5) return { color: '#10B981', label: 'Neutral' };
    return { color: '#3B82F6', label: 'Alkalisch' };
  };

  /**
   * Handle slider change - updates local state immediately, debounces parent update
   */
  const handleChange = (e) => {
    const newValue = parseFloat(e.target.value);

    // Update local state immediately for smooth UI
    setTempPhValue(newValue);

    // Clear any pending timeout
    if (phTimeoutRef.current) {
      clearTimeout(phTimeoutRef.current);
    }

    // Debounce the parent update (300ms)
    phTimeoutRef.current = setTimeout(() => {
      if (onChange) {
        onChange(newValue);
      }
    }, 300);
  };

  const colorData = getpHColor(tempPhValue);

  return (
    <div className="w-full px-2 py-2">
      {/* pH Label */}
      <label htmlFor="ph-slider" className="block text-xs font-medium text-gray-600 mb-2">
        pH-Wert
      </label>

      {/* Slider Container */}
      <div className="flex items-center gap-3">
        {/* Slider */}
        <div className="flex-1">
          <div className="relative">
            <input
              type="range"
              id="ph-slider"
              min="4.0"
              max="8.5"
              step="0.5"
              value={tempPhValue}
              onChange={handleChange}
              style={{
                background: `linear-gradient(to right, #10b981 0%, #10b981 ${((tempPhValue - 4) / 4.5) * 100}%, #e5e7eb ${((tempPhValue - 4) / 4.5) * 100}%, #e5e7eb 100%)`
              }}
              className="w-full h-2 rounded-full appearance-none cursor-pointer slider-emerald"
              aria-label="pH-Wert auswählen"
            />
          </div>
          {/* pH scale labels */}
          <div className="flex justify-between text-[10px] text-gray-500 mt-1 px-0.5">
            <span>4</span>
            <span>5</span>
            <span>6</span>
            <span>7</span>
            <span>8</span>
          </div>
        </div>

        {/* pH Value Display with Color */}
        <div className="w-14 text-right">
          <span
            className="text-sm font-semibold block"
            style={{ color: colorData.color }}
          >
            {tempPhValue.toFixed(1)}
          </span>
          <span
            className="text-[10px] block"
            style={{ color: colorData.color }}
          >
            {colorData.label}
          </span>
        </div>
      </div>

      {/* Clear pH Filter Button - show if pH filter is active (not null) */}
      {value !== null && (
        <button
          onClick={() => {
            setTempPhValue(7.0);
            if (phTimeoutRef.current) {
              clearTimeout(phTimeoutRef.current);
            }
            if (onChange) {
              onChange(null);
            }
          }}
          className="mt-2 text-xs text-emerald-600 hover:text-emerald-700 underline"
        >
          pH-Filter zurücksetzen
        </button>
      )}
    </div>
  );
};

export default React.memo(PhSlider);
